import { QueryWrapper } from '@diamond/shared/data-access';
import { BackofficeOrderDetail } from '@diamond/shared/types';
import { useAuthStore } from '@diamond/sol-admin/features/auth';

type UseActivitiesDataProps = {
  order_by: string;
  direction: string;
  size: number;
  page: number;
};

type UseActivitiesDetailOptions<S = BackofficeOrderDetail> = {
  select?: (data: BackofficeOrderDetail) => S;
  notifyOnChangeProps?: string[] | 'all' | (() => string[] | 'all' | undefined);
  notifyOnChangePropsExclusions?: string[];
  prefetchNextPage?: boolean;
};

type UseActivitiesDetailReturn<S = BackofficeOrderDetail> = {
  activitiesDetailData: S | undefined;
  pageCount: number;
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
};

export const useActivitiesDetail = <S = BackofficeOrderDetail,>(
  id: string,
  queryString: UseActivitiesDataProps,
  options?: UseActivitiesDetailOptions<S>
): UseActivitiesDetailReturn<S> => {
  const access_token = useAuthStore((state) => state.access_token);

  const queryActivities = QueryWrapper(`/backoffice/order/${id}`, access_token);

  const {
    data: activitiesDetailData,
    isLoading,
    pageCount,
    refetch,
    isFetching,
  } = queryActivities.useQueryGetAll<BackofficeOrderDetail, S>(
    `Activities-${id}`,
    queryString,
    0,
    '/activities/error',
    {
      staleTime: 30000,
      prefetchNextPage: options?.prefetchNextPage ?? false,
      enabled: !!id,
      select: options?.select as ((d: BackofficeOrderDetail) => S) | undefined,
      notifyOnChangeProps: options?.notifyOnChangeProps,
      notifyOnChangePropsExclusions: options?.notifyOnChangePropsExclusions,
    }
  );

  return {
    activitiesDetailData,
    pageCount,
    isLoading,
    isFetching,
    refetch,
  };
};

export default useActivitiesDetail;
